import { Button, Icon, StsIconName, Typography } from '@packages/ui/shared';
import { CVT } from '@packages/models/api/cvt';
import { DiagnosticCardLabel } from './diagnostic-card-label';
import { DiagnosticCardRightSideContent } from './diagnostic-card-right-side-content';
import { RadioCardGroup, RadioCardItem } from '@web/components/radio-card-group';
import { useTranslation } from 'react-i18next';

const RADIO_GROUP_NAME = 'cvt-diagnostic';

type DiagnosticCardGroupProps = {
    diagnosticOptions: CVT[];
    hasError: boolean;
    hasFailedToLoadMore: boolean;
    hasMore: boolean;
    isDisabled: boolean;
    isLoading: boolean;
    isLoadingMore: boolean;
    onLoadMore: () => void;
    onReload: () => void;
    onSelect: (diagnosticId: string) => void;
    selectedDiagnosticId?: string;
};

/**
 * The CVT diagnostics available to the technician for selection.
 *
 * Renders a radio card per diagnostic, or an empty state with a reload button when the list is empty or failed to load.
 * Loads more as the list is scrolled toward its end, with a "Try again" button when that fails.
 */
export function DiagnosticCardGroup({
    diagnosticOptions,
    hasError,
    hasFailedToLoadMore,
    hasMore,
    isDisabled,
    isLoading,
    isLoadingMore,
    onLoadMore,
    onReload,
    onSelect,
    selectedDiagnosticId,
}: DiagnosticCardGroupProps) {
    const { t } = useTranslation();

    const cards: RadioCardItem[] = hasError ? [] : diagnosticOptions.map(toRadioCardItem);
    const canLoadMoreOnScroll = hasMore && !hasFailedToLoadMore;

    const emptyStateDescription = hasError
        ? t(
              'qmr:sections.failCode.cvtDiagnostic.changeModal.loadError.description',
              'Something went wrong on our end. Please try again.'
          )
        : t(
              'qmr:sections.failCode.cvtDiagnostic.changeModal.emptyState.description',
              'Please run a new test before submitting.'
          );
    const emptyStateIconName: StsIconName = hasError ? 'refresh' : 'auto-cvt';
    const emptyStateTitle = hasError
        ? t('qmr:sections.failCode.cvtDiagnostic.changeModal.loadError.title', "We couldn't load the available tests")
        : t(
              'qmr:sections.failCode.cvtDiagnostic.changeModal.emptyState.title',
              'No tests from the last 30 days are available'
          );

    return (
        <>
            {cards.length > 0 && (
                <div className="mb-4 d-flex justify-content-between align-items-center">
                    <Typography variant="label">
                        {t(
                            'qmr:sections.failCode.cvtDiagnostic.changeModal.dayLimit',
                            'Only tests from the last 30 days are available'
                        )}
                    </Typography>

                    <Button
                        disabled={isLoading || isDisabled}
                        iconRight={<Icon color="blueOne" name="refresh" size={20} />}
                        isLoading={isLoading}
                        onPress={onReload}
                        variant="ghost-blue"
                    >
                        {t('qmr:sections.failCode.cvtDiagnostic.changeModal.reloadList', 'Reload list')}
                    </Button>
                </div>
            )}

            <RadioCardGroup
                ariaLabel={t(
                    'qmr:sections.failCode.cvtDiagnostic.changeModal.instructions',
                    'Select a test below to attach it.'
                )}
                cards={cards}
                isLoading={isLoading}
                name={RADIO_GROUP_NAME}
                onSelect={onSelect}
                selectedValue={selectedDiagnosticId}
                hasMore={canLoadMoreOnScroll}
                isDisabled={isDisabled}
                isLoadingMore={isLoadingMore}
                onLoadMore={onLoadMore}
                emptyStateIconName={emptyStateIconName}
                emptyStateTitle={emptyStateTitle}
                emptyStateContent={
                    <>
                        <Typography color="textDarkSecondary">{emptyStateDescription}</Typography>
                        <Button
                            disabled={isLoading}
                            isLoading={isLoading}
                            onPress={onReload}
                            variant="primary-blue-outline"
                        >
                            {t('qmr:sections.failCode.cvtDiagnostic.changeModal.reloadList', 'Reload list')}
                        </Button>
                    </>
                }
            />

            {hasFailedToLoadMore && (
                <div className="mt-2 d-flex align-items-center gap-2">
                    <Typography color="redOne" variant="caption">
                        {t(
                            'qmr:sections.failCode.cvtDiagnostic.changeModal.loadMoreError',
                            "We couldn't load more tests."
                        )}
                    </Typography>
                    <Button disabled={isDisabled} onPress={onLoadMore} variant="ghost-blue">
                        {t('qmr:sections.failCode.cvtDiagnostic.changeModal.buttons.tryAgain', 'Try again')}
                    </Button>
                </div>
            )}
        </>
    );
}

function toRadioCardItem(diagnostic: CVT): RadioCardItem {
    return {
        children: <DiagnosticCardLabel diagnostic={diagnostic} />,
        rightSideContent: <DiagnosticCardRightSideContent diagnostic={diagnostic} />,
        value: diagnostic.diagnosticId,
    };
}
